import React from 'react'

import {
  AlignItems,
  type BorderScale,
  BorderStyle,
  Box,
  Caption1,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontWeight,
  Icon,
  IconVariant,
  JustifyContent,
  type RadiusScale,
  TextAlign,
} from '@gousto-internal/citrus-react'

import type {
  OrderConfirmationRafStep,
  OrderConfirmationRafStepStatus,
} from '@library/api-customer-engagement-experience'

type RafStepperProgressProps = {
  steps?: OrderConfirmationRafStep[] | null
}

const NODE_APPEARANCE: Record<
  OrderConfirmationRafStepStatus,
  { bg: Color; borderColor: Color; textColor: Color }
> = {
  completed: { bg: Color.ColdGrey_700, borderColor: Color.White, textColor: Color.White },
  current: { bg: Color.Primary_600, borderColor: Color.White, textColor: Color.White },
  upcoming: { bg: Color.White, borderColor: Color.ColdGrey_100, textColor: Color.ColdGrey_800 },
}

// Nodes sit at index / (length - 1) along the track (space-between), so the fill must be
// derived from position, not a count of completed steps — a count-based fraction drifts from
// the node it's meant to reach for most step counts.
const getProgress = (steps: OrderConfirmationRafStep[]) => {
  if (steps.length <= 1) {
    return steps[0]?.status === 'upcoming' ? 0 : 1
  }

  const currentIndex = steps.findIndex((step) => step.status === 'current')

  if (currentIndex !== -1) {
    return currentIndex / (steps.length - 1)
  }

  const completedCount = steps.filter((step) => step.status === 'completed').length

  return completedCount > 0 ? (completedCount - 1) / (steps.length - 1) : 0
}

const StepNode = ({ step, index }: { step: OrderConfirmationRafStep; index: number }) => {
  const { bg, borderColor, textColor } = NODE_APPEARANCE[step.status]

  return (
    <Box
      data-testid={`raf-stepper-step-${index}`}
      data-status={step.status}
      width="28px"
      height="28px"
      flexShrink={0}
      // Needs a position or the absolutely-positioned track bars paint above it regardless of DOM order.
      position={DisplayPosition.Relative}
      display={Display.Flex}
      alignItems={AlignItems.Center}
      justifyContent={JustifyContent.Center}
      bg={bg}
      color={textColor}
      borderColor={borderColor}
      borderStyle={BorderStyle.Solid}
      borderWidth={0.5 as BorderScale}
      borderRadius="round"
    >
      {step.status === 'completed' ? (
        <Icon name="tick" variant={IconVariant.Inherit} size={4} />
      ) : (
        <Caption1 size={0} fontWeight={FontWeight.Bold} color={textColor}>
          {index + 1}
        </Caption1>
      )}
    </Box>
  )
}

const labelAlignment = (index: number, lastIndex: number): TextAlign => {
  if (index === 0) return TextAlign.Left
  if (index === lastIndex) return TextAlign.Right

  return TextAlign.Center
}

// Equal first/last width keeps the middle label centred (labels are content-width by default).
const EDGE_LABEL_WIDTH = '100px'

const labelWidth = (index: number, lastIndex: number): string | undefined =>
  index === 0 || index === lastIndex ? EDGE_LABEL_WIDTH : undefined

export const RafStepperProgress = ({ steps }: RafStepperProgressProps) => {
  if (!steps || steps.length === 0) {
    return null
  }

  return (
    <Box
      data-testid="raf-stepper-progress"
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={2}
    >
      <Box
        position={DisplayPosition.Relative}
        display={Display.Flex}
        alignItems={AlignItems.Center}
        justifyContent={JustifyContent.SpaceBetween}
        style={{ height: '28px' }}
      >
        <Box
          position={DisplayPosition.Absolute}
          width="100%"
          height="4px"
          borderRadius={4 as RadiusScale}
          bg={Color.ColdGrey_50}
        />
        <Box
          data-testid="raf-stepper-progress-fill"
          position={DisplayPosition.Absolute}
          height="4px"
          borderRadius={4 as RadiusScale}
          bg={Color.ColdGrey_800}
          width={`${getProgress(steps) * 100}%`}
        />
        {steps.map((step, index) => (
          <StepNode key={step.label} step={step} index={index} />
        ))}
      </Box>
      <Box display={Display.Flex} justifyContent={JustifyContent.SpaceBetween}>
        {steps.map((step, index) => {
          const isEdge = index === 0 || index === steps.length - 1

          return (
            <Box
              key={step.label}
              // minWidth 0 lets the middle label shrink/wrap; flex items default to min-width: auto.
              flexShrink={isEdge ? 0 : 1}
              minWidth={isEdge ? undefined : '0px'}
              width={labelWidth(index, steps.length - 1)}
            >
              <Caption1
                size={[0, 0, 2]}
                fontWeight={step.status === 'current' ? FontWeight.Bold : FontWeight.Normal}
                textAlign={labelAlignment(index, steps.length - 1)}
                color={Color.ColdGrey_800}
              >
                {step.label}
              </Caption1>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
